import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  WebSearchProvider,
  WebSearchQueryTrace,
  WebSearchTrace,
} from "@shaarp/common";
import { fullNumber } from "./format";

const PROVIDER_NAMES: Record<WebSearchProvider, string> = {
  exa: "Exa",
  tavily: "Tavily",
};

/** The provider that answered a query, with Exa's search type. */
const servedByLabel = (entry: WebSearchQueryTrace): string | null => {
  if (entry.servedBy === null) return null;
  const name = PROVIDER_NAMES[entry.servedBy];
  return entry.exaType ? `${name} ${entry.exaType}` : name;
};

/**
 * How a `webSearch` call was answered, query by query: the provider that
 * served it, whether it stood in for the route's primary, and how fast. What
 * the `[SearchRoute]` server logs say, without leaving the panel.
 */
export function SearchTrace({ trace }: { trace: WebSearchTrace }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1">
      <p className="text-muted-foreground">
        {t("chat.debug.messages.searchTrace.route", {
          intent: t(`chat.debug.messages.searchTrace.intents.${trace.intent}`),
          primary: PROVIDER_NAMES[trace.primary],
        })}
      </p>
      <ul className="space-y-1">
        {trace.queries.map((entry, index) => {
          const servedBy = servedByLabel(entry);
          return (
            <li
              className="flex flex-wrap items-center gap-x-2 gap-y-1"
              key={`${entry.query}-${index}`}
            >
              <span className="min-w-0 truncate font-mono" title={entry.query}>
                {entry.query}
              </span>
              {servedBy && <Badge variant="secondary">{servedBy}</Badge>}
              {entry.isFallback && (
                <Badge variant="outline">
                  {t("chat.debug.messages.searchTrace.fallback")}
                </Badge>
              )}
              {entry.failed && (
                <Badge variant="destructive">
                  {t("chat.debug.messages.searchTrace.failed")}
                </Badge>
              )}
              <span className="text-muted-foreground">
                {t("chat.debug.messages.searchTrace.results", {
                  count: entry.resultCount,
                  durationMs: fullNumber(entry.durationMs),
                })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
